import { UserProfile } from '../services/api';

export const normalizeEmail = (email: string | undefined): string =>
  (email || '').trim().toLowerCase();

const firstNonEmpty = (
  group: UserProfile[],
  pick: (user: UserProfile) => string | undefined
): string => group.map(pick).find((value) => !!value) || '';

// Collapse duplicate accounts sharing an email address: identity and signup date
// come from the oldest row, activity counts are summed, and fields missing on the
// oldest row are filled from newer duplicates. Users without an email are kept as-is.
export const mergeUsersByEmail = (users: UserProfile[]): UserProfile[] => {
  const groups = new Map<string, UserProfile[]>();

  users.forEach((user, index) => {
    const key = normalizeEmail(user.email) || `__no-email-${index}`;
    const group = groups.get(key);
    if (group) {
      group.push(user);
    } else {
      groups.set(key, [user]);
    }
  });

  return Array.from(groups.values()).map((group) => {
    if (group.length === 1) {
      return group[0];
    }

    const byAge = [...group].sort((a, b) => {
      const aTime = a.createdAt ? a.createdAt.getTime() : Number.POSITIVE_INFINITY;
      const bTime = b.createdAt ? b.createdAt.getTime() : Number.POSITIVE_INFINITY;
      return aTime - bTime;
    });

    return {
      ...byAge[0],
      email: normalizeEmail(byAge[0].email),
      displayName: firstNonEmpty(byAge, (user) => user.displayName),
      connection: firstNonEmpty(byAge, (user) => user.connection),
      zip_code: firstNonEmpty(byAge, (user) => user.zip_code),
      ward: firstNonEmpty(byAge, (user) => user.ward),
      address: firstNonEmpty(byAge, (user) => user.address),
      race_ethnicity: firstNonEmpty(byAge, (user) => user.race_ethnicity),
      ideasCount: group.reduce((sum, user) => sum + (user.ideasCount ?? 0), 0),
      commentsCount: group.reduce((sum, user) => sum + (user.commentsCount ?? 0), 0),
      likesCount: group.reduce((sum, user) => sum + (user.likesCount ?? 0), 0),
    };
  });
};
